import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Field, reduxForm } from 'redux-form'

import { inputField, emailField, select, legalField } from './fields'
import { fieldOptions } from './field_options'

class Signup extends Component {
  constructor(props) {
    super(props)
    this.turnstileWidgetId = null
    this.turnstileContainerRef = React.createRef()
    this.state = {
      turnstileMessage: null,
    }
  }

  componentDidMount() {
    this.loadTurnstile()
  }

  componentDidUpdate(prevProps) {
    const hasNewTurnstileError =
      this.props.errorMessage &&
      this.props.errorMessage !== prevProps.errorMessage &&
      this.props.errorMessage.toLowerCase().includes('security check')

    if (hasNewTurnstileError) {
      this.resetTurnstile('Please complete the security check and try again.')
    }
  }

  componentWillUnmount() {
    if (this.turnstileWidgetId !== null && window.turnstile) {
      window.turnstile.remove(this.turnstileWidgetId)
    }
  }

  resetTurnstile(message = null) {
    if (this.turnstileWidgetId !== null && window.turnstile) {
      window.turnstile.reset(this.turnstileWidgetId)
    }
    this.props.change('turnstileToken', '')
    this.setState({ turnstileMessage: message })
  }

  loadTurnstile() {
    // If turnstile script is already loaded, render the widget
    if (window.turnstile) {
      this.renderTurnstileWidget()
      return
    }

    // Load Turnstile script
    const script = document.createElement('script')
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => this.renderTurnstileWidget()
    document.head.appendChild(script)
  }

  renderTurnstileWidget() {
    if (!window.turnstile || !this.turnstileContainerRef.current) return

    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY
    if (!siteKey) {
      console.warn('REACT_APP_TURNSTILE_SITE_KEY not set')
      return
    }

    this.turnstileWidgetId = window.turnstile.render(
      this.turnstileContainerRef.current,
      {
        sitekey: siteKey,
        theme: 'light',
        callback: (token) => {
          this.setState({ turnstileMessage: null })
          this.props.change('turnstileToken', token)
        },
        'expired-callback': () => {
          this.resetTurnstile('Security check expired. Please try again.')
        },
        'error-callback': () => {
          this.resetTurnstile(
            'Security check failed to load. Please try again.'
          )
        },
        'timeout-callback': () => {
          this.resetTurnstile('Security check timed out. Please try again.')
        },
      }
    )
  }

  render() {
    const {
      width,
      onSignup,
      errorMessage,
      handleSubmit,
      submitting,
      successMessage,
    } = this.props

    if (successMessage) {
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            height: '100vh',
            background: '#f5f5f5',
          }}
        >
          <div
            className="col-sm-6 col-lg-4 p-0"
            style={{
              background: 'white',
              boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.18)',
            }}
          >
            <div className="py-5 px-4 text-center">
              <div className="d-flex justify-content-center mb-3">
                <img
                  src="/images/logo-fit3d.png"
                  alt="Fit3D"
                  style={{ width: '60px' }}
                />
              </div>
              <h3 className="mb-3">Account Created!</h3>
              <p className="text-muted mb-4">
                Your account has been created successfully. Log in to get
                started.
              </p>
              <button
                type="button"
                className="btn btn-block"
                style={{ backgroundColor: '#f7940d', color: 'white' }}
                onClick={this.props.onLoginClick}
              >
                Log In Now
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div
        className="d-flex align-items-center"
        style={{
          minHeight: width && width < 500 ? null : '100vh',
          background: '#f5f5f5',
        }}
      >
        <div className="col-sm-6 offset-sm-3 col-lg-4 offset-lg-4 p-0">
          <div
            className="py-4 px-4"
            style={{
              background: 'white',
              boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.18)',
            }}
          >
            <div className="d-flex justify-content-center mb-3">
              <div>
                <img
                  src="/images/logo-fit3d.png"
                  alt="Fit3D"
                  style={{ width: '60px' }}
                />
              </div>
            </div>
            <div className="text-center mb-4">
              <h3>Create a Fit3D Account</h3>
            </div>
            <form
              onSubmit={handleSubmit((data) => {
                if (!data.turnstileToken) {
                  this.resetTurnstile(
                    'Please complete the security check before continuing.'
                  )
                  return
                }

                if (
                  this.turnstileWidgetId !== null &&
                  window.turnstile &&
                  window.turnstile.isExpired &&
                  window.turnstile.isExpired(this.turnstileWidgetId)
                ) {
                  this.resetTurnstile(
                    'Security check expired. Please try again.'
                  )
                  return
                }

                onSignup(data)
              })}
            >
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Field
                    name="firstName"
                    className="form-control"
                    component={inputField}
                    label="First Name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <Field
                    name="lastName"
                    className="form-control"
                    component={inputField}
                    label="Last Name"
                  />
                </div>
              </div>
              <div className="form-group">
                <Field
                  name="realEmail"
                  type="email"
                  className="form-control"
                  component={emailField}
                  label="E-mail"
                />
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Field
                    name="password"
                    type="password"
                    className="form-control"
                    component={inputField}
                    label="Password (8+ characters)"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <Field
                    name="confirmPassword"
                    type="password"
                    className="form-control"
                    component={inputField}
                    label="Confirm Password"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <Field
                    name="age"
                    type="number"
                    className="form-control"
                    component={inputField}
                    label="Age"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <Field
                    name="gender"
                    className="form-control"
                    component={select}
                    label="Gender"
                    options={fieldOptions.gender}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <Field
                    name="units"
                    className="form-control"
                    component={select}
                    label="Units"
                    options={fieldOptions.units}
                  />
                </div>
              </div>
              <div className="mb-3">
                <Field
                  name="privacyPolicyAccepted"
                  className="form-control"
                  component={legalField}
                  page="Privacy Policy"
                  externalPage="https://www.fit3d.com/privacy/"
                />
                <Field
                  name="termsAccepted"
                  className="form-control"
                  component={legalField}
                  page="Terms of Use"
                  externalPage="https://www.fit3d.com/terms/"
                />
              </div>
              <div className="mb-3" ref={this.turnstileContainerRef} />
              {this.state.turnstileMessage && (
                <div className="text-danger mb-3">
                  {this.state.turnstileMessage}
                </div>
              )}
              {errorMessage && (
                <div className="text-danger mb-3">{errorMessage}</div>
              )}
              <div className="row mx-0 mb-3">
                <button
                  type="submit"
                  className="btn btn-block"
                  style={{ backgroundColor: '#f7940d', color: 'white' }}
                  disabled={submitting}
                >
                  Create Account
                </button>
              </div>
              <div className="text-center">
                <small className="text-muted">
                  Already have an account?{' '}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (this.props.onLoginClick) this.props.onLoginClick()
                    }}
                    style={{ color: '#f7940d' }}
                  >
                    Log in
                  </a>
                </small>
              </div>
            </form>
          </div>
        </div>
      </div>
    )
  }
}

Signup.propTypes = {
  width: PropTypes.number,
  onSignup: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  successMessage: PropTypes.bool,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  change: PropTypes.func,
  onLoginClick: PropTypes.func,
}

export default reduxForm({
  form: 'signupForm',
})(Signup)
